import { useEffect, useMemo, useRef, useState } from 'react';
import { priceHiredEquipmentLines } from '../../../services/hiredEquipmentService';
import type { HeSameDayHours, HiredEquipmentGridRow } from '../types';

/**
 * C4 / M6 – keep each grid line priced at the rate in effect ON ITS DATE, and run the same-day
 * hours check, using the server's pricing service (POST /api/hired-equipment/price-lines).
 *
 * The client never decides a rate: the catalogue rate a resource is picked with is replaced by the
 * dated rate as soon as the server answers, and submit / approval re-price on the server anyway.
 * Debounced, and only re-run when something that affects pricing changes (resource, date, task,
 * qty, unit), so typing in other cells does not refetch.
 */
export function useHeLinePricing(
  rows: HiredEquipmentGridRow[],
  setRows: (updater: (prev: HiredEquipmentGridRow[]) => HiredEquipmentGridRow[]) => void,
  options: {
    serviceAreaId: number | null | undefined;
    excludeFormId?: string | null;
    /** M4: the saved draft being finished, so its "left as is" choices price the lines. */
    draftId?: string | null;
    /** Re-price the lines (write the dated rate onto them). */
    enabled?: boolean;
    /**
     * H4: still fetch the compliance warnings when pricing is off, e.g. an approver reviewing a
     * submission read-only. Defaults to `enabled`.
     */
    checkCompliance?: boolean;
  },
): { sameDayHours: HeSameDayHours[]; pricingError: string | null } {
  const [sameDayHours, setSameDayHours] = useState<HeSameDayHours[]>([]);
  const [pricingError, setPricingError] = useState<string | null>(null);
  const requestSeq = useRef(0);
  const enabled = options.enabled !== false;
  const checkCompliance = options.checkCompliance ?? enabled;

  const signature = useMemo(
    () =>
      JSON.stringify(
        rows.map((row) => [row.clientId, row.lumpSum, row.resourceId, row.vendorId, row.date, row.taskId, row.qty, row.unit]),
      ),
    [rows],
  );

  useEffect(() => {
    if (!enabled && !checkCompliance) return;
    const checkable = rows.filter((row) => row.date && (row.resourceId != null || row.vendorId != null));
    if (checkable.length === 0) {
      setSameDayHours([]);
      return;
    }
    const seq = ++requestSeq.current;
    const timer = setTimeout(async () => {
      try {
        const result = await priceHiredEquipmentLines({
          serviceAreaId: options.serviceAreaId ?? null,
          excludeFormId: options.excludeFormId ?? null,
          draftId: options.draftId ?? null,
          lines: rows.map((row) => ({
            resourceId: row.lumpSum ? null : row.resourceId,
            resourceLabel: row.resourceLabel,
            vendorId: row.vendorId ?? null,
            taskId: row.taskId,
            date: row.date,
            qty: row.qty,
            unit: row.unit,
            lumpSum: row.lumpSum,
          })),
        });
        if (seq !== requestSeq.current) return;
        setPricingError(null);
        if (enabled) setSameDayHours(result.sameDay);
        const byClientId = new Map(rows.map((row, index) => [row.clientId, result.prices[index]]));
        const warningsByClientId = new Map(
          rows.map((row, index) => [row.clientId, result.complianceWarnings?.[index] ?? []]),
        );
        setRows((prev) => {
          let changed = false;
          const next = prev.map((current) => {
            // H4: warnings apply to every line, priced or not.
            const warnings = warningsByClientId.get(current.clientId);
            let row = current;
            if (warnings && JSON.stringify(current.complianceWarnings ?? []) !== JSON.stringify(warnings)) {
              changed = true;
              row = { ...current, complianceWarnings: warnings };
            }
            if (!enabled) return row;
            const price = byClientId.get(row.clientId);
            if (!price || row.lumpSum || row.resourceId == null) return row;
            const rateWindow =
              price.rate != null
                ? {
                    effectiveFrom: price.window?.effectiveFrom ?? '',
                    effectiveTo: price.window?.effectiveTo ?? null,
                    basis: price.basis === 'component-sum' ? ('component-sum' as const) : ('own-rate' as const),
                  }
                : null;
            const noRateOnDate = price.rate == null;
            const rate = price.rate ?? null;
            if (
              row.rate === rate &&
              row.noRateOnDate === noRateOnDate &&
              JSON.stringify(row.rateWindow ?? null) === JSON.stringify(rateWindow)
            ) {
              return row;
            }
            changed = true;
            return { ...row, rate, rateWindow, noRateOnDate };
          });
          return changed ? next : prev;
        });
      } catch (error) {
        if (seq !== requestSeq.current) return;
        console.error('Failed to price hired equipment lines', error);
        setPricingError('Could not check rates for the line dates – try again or contact support.');
      }
    }, 350);
    return () => clearTimeout(timer);
    // `signature` captures every pricing input; rows themselves change on every keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature, options.serviceAreaId, options.excludeFormId, options.draftId, enabled, checkCompliance]);

  return { sameDayHours, pricingError };
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "Aug 1 – Sep 30, 2026" / "from Oct 1, 2026". */
export function formatRateWindow(window: { effectiveFrom: string; effectiveTo: string | null } | null | undefined): string {
  if (!window?.effectiveFrom) return '';
  // Built by hand rather than toLocaleDateString: ICU month abbreviations differ between Node /
  // browser versions ("Aug" vs "Aug."), and this text is asserted in tests that run on CI.
  const fmt = (iso: string, withYear: boolean) => {
    const [y, m, d] = iso.split('-').map(Number);
    if (!y || !m || !d || m < 1 || m > 12) return iso;
    return `${MONTHS[m - 1]} ${d}${withYear ? `, ${y}` : ''}`;
  };
  if (!window.effectiveTo) return `from ${fmt(window.effectiveFrom, true)}`;
  const sameYear = window.effectiveFrom.slice(0, 4) === window.effectiveTo.slice(0, 4);
  return `${fmt(window.effectiveFrom, !sameYear)} – ${fmt(window.effectiveTo, true)}`;
}
